import React, { useState, useMemo } from 'react';
import { Form, Input, Button, Icon, Checkbox, Tooltip, DatePicker, Switch, Popover } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { PayoutLink } from '../../../../models';
import { connect } from 'react-redux';
import { alertify, handleApiExceptions } from '../../../../app_methods';
import { MerchantService } from '../../../../services';
import moment from 'moment';
import { useEffect } from 'react';
import { Text3, Text4 } from '../../../../styles';

const beneficiaryTypes = [
    { label: "IMPS", value: "imps", disabled: false },
    { label: "NEFT", value: "neft", disabled: false },
    { label: "RTGS", value: "rtgs", disabled: false },
    { label: "UPI", value: "upi", disabled: false },
];

let PayoutLinkForm = (props) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [amount, setAmount] = useState();
    const [allowedBeneficiaryTypes, setAllowedBeneficiaryTypes] = useState(beneficiaryTypes);
    const [showPayeeDetailsMandatoryMessage, setShowPayeeDetailsMandatoryMessage] = useState(false);

    const { form, payout_link } = props;
    const { getFieldDecorator } = form;
    const validations = PayoutLink.validations;
    const merchant_payment_modes =  props.current_user.payment_modes;
    const payment_modes_transaction_limit = props.current_user.tl;
    const formValues = form.getFieldsValue();

    useEffect(() => {
        setAllowedBeneficiaryTypes((prev) => {
            return prev.map((type) => {
                const isBeneficiaryAllowed = !merchant_payment_modes.includes(type.value?.toUpperCase())
                    ? false
                    : checkBeneficiaryAllowed(amount, type.value);
                return { ...type, disabled: !isBeneficiaryAllowed };
            });
        });
    }, [amount, props.current_user]);

    useEffect(() => {
        form.setFieldsValue({
            allowed_beneficiary_types: allowedBeneficiaryTypes.filter((type) => !type.disabled).map((type) => type.value),
        });
    }, [allowedBeneficiaryTypes]);

    const isSubmitDisabled = useMemo(() => {
        return !allowedBeneficiaryTypes.some((type) => !type.disabled);
    }, [allowedBeneficiaryTypes]);

    const onSubmit = (e) => {
        e.preventDefault();
        form.validateFields((err, values) => {
            const isPayeeMissing = !values.payee_email && !values.payee_phone;

            if (err || isPayeeMissing) {
                if (isPayeeMissing) {
                    setShowPayeeDetailsMandatoryMessage(true);
                }
                return;
            }
            props.onSubmit(form.getFieldsValue());
        });
    }

    const generateUniqueCode = () => {
        setIsGenerating(true);
        MerchantService.generate_ulc().then(response => {
            if (response.success) form.setFieldsValue({ "unique_code": response.data.unique_link_code })
            setIsGenerating(false);
        }).catch(error => {
            setIsGenerating(false);
            handleApiExceptions(error);
        })
    };

    const renderUniqueCodeExtra = () => {
        return (
            <Tooltip title="Generate Random">
                <Icon type="setting" spin={isGenerating} onClick={generateUniqueCode} />
            </Tooltip>
        )
    };

    const checkBeneficiaryAllowed = (amount, mode) => {
        if (amount === undefined) {
            return true;
        }
        if (mode === "imps") {
            return (
                amount >= 1 &&
                amount <= (payment_modes_transaction_limit["IMPS"] ?? Infinity) &&
                merchant_payment_modes.includes("IMPS")
            );
        }
        if (mode === "neft") {
            return (
                amount >= 1 &&
                amount <= (payment_modes_transaction_limit["NEFT"] ?? Infinity) &&
                merchant_payment_modes.includes("NEFT")
            );
        }
        if (mode === "rtgs") {
            return (
                amount >= 200000 &&
                amount <= (payment_modes_transaction_limit["RTGS"] ?? Infinity) &&
                merchant_payment_modes.includes("RTGS")
            );
        }
        if (mode === "upi") {
            return (
                amount >= 1 &&
                amount <= (payment_modes_transaction_limit["UPI"] ?? 100000) &&
                merchant_payment_modes.includes("UPI")
            );
        }
    };

    const renderUniqueCodeLabel = ({label, content, iconType}) => {
        return (
            <div className="d-inline">
                <span className="mr-1">{label}</span>
                <Popover
                    overlayStyle={{ maxWidth: 600 }}
                    content={content}
                    placement="top"
                >
                    <Icon type={iconType} />
                </Popover>
            </div>
        )
    };

    const renderSkipAuthenticationLabel = () => {
        return (
            <div className="d-inline">
                <span className="mr-1">Skip Authentication:</span>
                <Popover
                    overlayStyle={{ maxWidth: 600 }}
                    content="Authentication code will be generated for this link which you need to send to the payee for executing the payout link. You can skip authentication by below toggle button."
                    placement="top"
                >
                    <Icon type="info-circle" />
                </Popover>
            </div>
        )
    };

    const handleInputChange = (value, fieldKey) => {
        if (fieldKey === "payee_email" && !value && !formValues["payee_phone"]) {
            setShowPayeeDetailsMandatoryMessage(true);
        } else if (fieldKey === "payee_phone" && !value && !formValues["payee_email"]) {
            setShowPayeeDetailsMandatoryMessage(true);
        } else {
            setShowPayeeDetailsMandatoryMessage(false);
        }
    };

    return (
        <Form onSubmit={onSubmit}>
            {getFieldDecorator("id", {
                initialValue: payout_link.id
            })(<Input autoComplete='off' hidden />)}

            <div className="row">
                <div className="col-sm-12 col-md-12 col-lg-12">
                    <Form.Item label="Payee Name">
                        {getFieldDecorator("payee_name", {
                            rules: validations.payee_name
                        })(<Input autoComplete='off' prefix={<Icon type="profile" />} />)}
                    </Form.Item>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12 mb-1">
                    <Text3 strong>
                        <span className="text-danger font-weight-normal">* </span>
                        Payee Details
                    </Text3>
                </div>
                <div className="col-sm-12 col-md-6 col-lg-6">
                    <Form.Item label="Payee Email ID">
                        {getFieldDecorator("payee_email", {
                            rules: validations.payee_email,
                        })(
                            <Input
                                onChange={(e) => handleInputChange(e.target.value, "payee_email")}
                                autoComplete="off"
                                prefix={<Icon type="mail" />}
                            />,
                        )}
                    </Form.Item>
                </div>
                <div className="col-sm-12 col-md-6 col-lg-6">
                    <Form.Item label="Payee Phone Number">
                        {getFieldDecorator("payee_phone", {
                            rules: validations.payee_phone,
                        })(
                            <Input
                                onChange={(e) => handleInputChange(e.target.value, "payee_phone")}
                                autoComplete="off"
                                prefix={<Icon type="phone" />}
                            />,
                        )}
                    </Form.Item>
                </div>
                <div className="col-sm-12">
                    {showPayeeDetailsMandatoryMessage && (
                        <Text4 style={{ position: "relative", bottom: 14, color: "#f5222d" }}>
                            Either Email ID or Phone Number is mandatory.
                        </Text4>
                    )}
                </div>
            </div>
            
            <div className="row">
                <div className="col-sm-12 col-md-6 col-lg-6">
                    <Form.Item label="Amount">
                        {getFieldDecorator("amount", {
                            rules: validations.amount,
                        })(
                            <Input
                                autoComplete="off"
                                onChange={(e) => {
                                    setAmount(+e.target.value);
                                }}
                                prefix={<span dangerouslySetInnerHTML={{ __html: `&#8377; ` }} />}
                            />,
                        )}
                    </Form.Item>
                </div>
                <div className="col-sm-12 col-md-6 col-lg-6">
                    <Form.Item
                        colon={false}
                        label={renderUniqueCodeLabel({
                            label: "Unique Code:",
                            content:
                                "You can enter unique name for your link. e.g. cashback-for-grocery, refund-link etc. You can autogenerate it by clicking on the cog button.",
                            iconType: "info-circle",
                        })}
                    >
                        {getFieldDecorator("unique_code", {
                            rules: validations.unique_code,
                        })(
                            <Input
                                autoComplete="off"
                                prefix={<Icon type="audit" />}
                                addonAfter={renderUniqueCodeExtra()}
                            />,
                        )}
                    </Form.Item>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12 col-md-6 col-lg-6">
                    <Form.Item label="Expiry Date">
                        {getFieldDecorator("expiry_date", {
                            rules: validations.expiry_date
                        })(<DatePicker style={{ width: "100%" }} disabledDate={(date) => date.valueOf() <= moment().valueOf()} />)}
                    </Form.Item>
                </div>
                <div className="col-sm-12 col-md-6 col-lg-6">
                    <Form.Item label="Notify Payee">
                        {getFieldDecorator("notify_payee", {
                            valuePropName: "checked",
                            initialValue: false
                        })(<Switch checkedChildren="Yes" unCheckedChildren="No" />)}
                    </Form.Item>
                </div>
                {/* <div className="col-sm-12 col-md-6 col-lg-6">
                    <Form.Item colon={false} label={renderSkipAuthenticationLabel()}>
                        {getFieldDecorator("skip_authentication", {
                            rules: validations.skip_authentication,
                            valuePropName: "checked",
                            initialValue: false
                        })(<Switch checkedChildren="Yes" unCheckedChildren="No" />)}
                    </Form.Item>
                </div> */}
            </div>

            <div className="row">
                <div className="col-sm-12 col-md-12 col-lg-12">
                    <Form.Item
                        colon={false}
                        label={renderUniqueCodeLabel({
                            label: "Allowed Beneficiary Types:",
                            content: <div>Available payout modes are based on <br /> backend configurations and limits.</div>,
                            iconType: "question-circle",
                        })}
                    >
                        {getFieldDecorator("allowed_beneficiary_types", {
                            rules: validations.allowed_beneficiary_types,
                            initialValue: ["imps", "neft", "rtgs", "upi"],
                        })(<Checkbox.Group options={allowedBeneficiaryTypes} />)}
                    </Form.Item>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12 col-md-12 col-lg-12">
                    <Form.Item label="Description">
                        {getFieldDecorator("description", {
                            rules: validations.description
                        })(<TextArea ows={1} />)}
                    </Form.Item>
                </div>
            </div>
            <div className="row">
                <div className="col-sm-12 col-md-12 col-lg-12" style={{fontSize: "12px"}}>
                    <b>Note: </b><br></br>
                    <i>Payout link will deactivate automatically after 3 failed attempts.*</i>
                </div>
            </div>
            <div className="w-100 d-flex justify-content-end mt-2">
                <Button disabled={isSubmitDisabled} type="primary" htmlType="submit" loading={props.loading}>
                    Create
                </Button>
            </div>
        </Form >
    )
};

PayoutLinkForm.defaultProps = {
    payout_link: {}
}

const mapStateToProps = state => ({
    current_user: state.userReducer.current_user
});

PayoutLinkForm = Form.create()(PayoutLinkForm);

export default connect(mapStateToProps)(PayoutLinkForm);